import { useEffect, useRef, useState } from "react";
import { Modal, Progress, Tag, Typography, message } from "antd";
import { completeRetouchUpload, createRetouchUploadBatch, syncRetouchReviewPublic, uploadRetouchFile } from "../../utils/retouchReviewApi";
import { fileNameFromPath, isJpgRetouchFile } from "../../utils/retouchReviewUpload";

function RetouchSingleUploadModal({ open, photo, reviewId, token, copy, onClose, onUploaded }) {
    const [file, setFile] = useState(null);
    const [mismatchFile, setMismatchFile] = useState(null);
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState(0);
    const inputRef = useRef(null);
    const expectedName = fileNameFromPath(photo?.relativePath);

    useEffect(() => {
        if (!open) {
            setFile(null);
            setMismatchFile(null);
            setRunning(false);
            setProgress(0);
            if (inputRef.current) inputRef.current.value = "";
        }
    }, [open]);

    const close = () => {
        if (!running) onClose?.();
    };

    const choose = (event) => {
        const selected = event.target.files?.[0] || null;
        event.target.value = "";
        if (!selected) return;
        if (!isJpgRetouchFile(selected)) {
            setFile(null);
            message.error(copy.singleFileFormatError);
            return;
        }
        if (selected.name !== expectedName) {
            setFile(null);
            setMismatchFile(selected);
            return;
        }
        setFile(selected);
    };

    const confirmMismatch = () => {
        setFile(mismatchFile);
        setMismatchFile(null);
    };

    const upload = async () => {
        if (!file || !photo || !reviewId || running) return;
        setRunning(true);
        setProgress(0);
        try {
            const created = await createRetouchUploadBatch({
                id: reviewId,
                token,
                mode: "single",
                files: [{ version: "latest", relativePath: photo.relativePath, size: file.size }],
            });
            const resolvedVersion = created.files?.[0]?.version;
            if (!resolvedVersion) throw new Error(copy.uploadFailed);
            await uploadRetouchFile({
                batchId: created.batchId,
                token,
                file,
                version: resolvedVersion,
                relativePath: photo.relativePath,
                onProgress: setProgress,
            });
            const completed = await completeRetouchUpload({ batchId: created.batchId, token });
            if (completed.status !== "completed") throw new Error(copy.uploadFailed);
            await syncRetouchReviewPublic({ id: reviewId, token });
            message.success(copy.autoSyncDone);
            await onUploaded?.();
            onClose?.();
        } catch (error) {
            message.error(error.message || copy.uploadFailed);
        } finally {
            setRunning(false);
        }
    };

    return <>
        <Modal
            open={open}
            title={copy.uploadCurrentTitle}
            onCancel={close}
            onOk={upload}
            okText={copy.startUpload}
            cancelText={copy.cancel}
            confirmLoading={running}
            okButtonProps={{ disabled: !file }}
            maskClosable={!running}
            closable={!running}
            destroyOnClose
        >
            <Typography.Paragraph>{copy.singleUploadFormatHint}</Typography.Paragraph>
            <Typography.Paragraph type="secondary">
                {copy.singleUploadFilenameHint.replace("{filename}", expectedName)}
            </Typography.Paragraph>
            <input ref={inputRef} type="file" accept=".jpg,image/jpeg" onChange={choose} disabled={running} />
            {file && <div className="retouch-upload-file"><span className="retouch-upload-file__name" title={file.name}>{file.name}</span><Tag>{copy.selected}</Tag></div>}
            {running && <Progress percent={Math.round(progress * 100)} />}
        </Modal>
        <Modal
            open={Boolean(mismatchFile)}
            title={copy.singleFilenameMismatchTitle}
            onOk={confirmMismatch}
            onCancel={() => setMismatchFile(null)}
            okText={copy.singleFilenameMismatchConfirm}
            cancelText={copy.cancel}
            maskClosable={false}
        >
            <Typography.Paragraph>
                {copy.singleFilenameMismatch
                    .replace("{selected}", mismatchFile?.name || "")
                    .replace("{expected}", expectedName)}
            </Typography.Paragraph>
            <Typography.Paragraph type="warning">{copy.singleFilenameMismatchHint}</Typography.Paragraph>
        </Modal>
    </>;
}

export default RetouchSingleUploadModal;
